import { Injectable } from '@angular/core';
import { ActiveToast, IndividualConfig, ToastrService } from 'ngx-toastr';

/** How long an error popup stays up, in ms (US 36098: long enough to read and copy). */
export const ERROR_TOAST_TIMEOUT_MS = 10000;

/**
 * Options every error popup shares (US 36098): stays 10 s, hovering keeps it open
 * (extendedTimeOut) so the text can be selected and copied, a close (X) dismisses it
 * sooner, and clicking the text does not dismiss it. Success/info/warning are unchanged.
 */
export const ERROR_TOAST_OPTIONS: Partial<IndividualConfig> = {
  timeOut: ERROR_TOAST_TIMEOUT_MS,
  extendedTimeOut: ERROR_TOAST_TIMEOUT_MS,
  closeButton: true,
  tapToDismiss: false,
  progressBar: true,
};

@Injectable({
  providedIn: 'root',
})
export class MessageHelper {
  constructor(private toaster: ToastrService) {}

  error(message: string, title = 'Error!', timeOut = ERROR_TOAST_TIMEOUT_MS) {
    this.toaster.error(message, title, {
      enableHtml: true,
      ...ERROR_TOAST_OPTIONS,
      timeOut,
    });
  }

  success(message: string, title = 'Success!', timeOut = 5000) {
    this.toaster.success(message, title, {
      enableHtml: true,
      timeOut,
    });
  }

  warning(message: string, title = 'Warning!', timeOut = 5000) {
    this.toaster.warning(message, title, {
      enableHtml: true,
      timeOut,
    });
  }

  /**
   * Warning toast that stays until the user closes it (close button or tap) or the
   * caller closes it via the returned handle, instead of vanishing after a timeout.
   */
  warningSticky(message: string, title = 'Warning!'): ActiveToast<unknown> {
    return this.toaster.warning(message, title, {
      enableHtml: true,
      disableTimeOut: true,
      closeButton: true,
      tapToDismiss: true,
    });
  }

  /** Success toast that stays until the user closes it, for notices that carry a link to act on. */
  successSticky(message: string, title = 'Success!'): ActiveToast<unknown> {
    return this.toaster.success(message, title, {
      enableHtml: true,
      disableTimeOut: true,
      closeButton: true,
      tapToDismiss: false,
    });
  }

  information(message: string, title = 'Information!', timeOut = 5000) {
    this.toaster.info(message, title, {
      enableHtml: true,
      timeOut,
    });
  }
}
